import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import { canWriteStammdaten } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { Button } from "@/components/ui/Button";
import { ClickableTableRow } from "@/components/ui/ClickableTableRow";
import { Table } from "@/components/ui/Table";
import { CreateCategoryModal } from "@/pages/stammdaten/CreateCategoryModal";
import { EditCategoryModal } from "@/pages/stammdaten/EditCategoryModal";
import { StammdatenWriteHint } from "@/pages/stammdaten/StammdatenWriteHint";
import { categoryColorOrDefault } from "@/lib/pitchCategoryColors";
import { rentalPeriodLabel } from "@/lib/rentalPeriod";
import type { PitchCategory } from "@/types/camping";

export function CategoriesPage() {
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canWrite = canWriteStammdaten(groups);
  const [categories, setCategories] = useState<PitchCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<PitchCategory | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<PitchCategory[]>("/api/v1/pitch-categories?include_inactive=true")
      .then(setCategories)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPageMeta({ title: "Stammdaten", subtitle: "Platzkategorien" });
    load();
    return () => setPageMeta(null);
  }, [load, setPageMeta]);

  return (
    <div className="space-y-6">
      <EditCategoryModal
        open={!!selected}
        category={selected}
        onClose={() => setSelected(null)}
        onSaved={load}
        canEdit={canWrite}
      />
      <CreateCategoryModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
      />

      {!canWrite ? <StammdatenWriteHint /> : null}

      {canWrite ? (
        <div className="flex justify-end">
          <Button type="button" className="w-full sm:w-auto" onClick={() => setCreateOpen(true)}>
            Neue Kategorie
          </Button>
        </div>
      ) : null}

      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      {loading ? (
        <p className="text-sm text-label-secondary">Laden…</p>
      ) : (
        <Table wide emptyMessage="Keine Kategorien." isEmpty={categories.length === 0}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Farbe</th>
              <th>Zeitraum</th>
              <th>Sortierung</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((c) => (
              <ClickableTableRow
                key={c.id}
                ariaLabel={`Kategorie ${c.name} öffnen`}
                onActivate={() => setSelected(c)}
              >
                <td className="font-medium">{c.name}</td>
                <td>
                  <span
                    className="inline-block h-4 w-4 rounded-full border border-separator/60"
                    style={{ backgroundColor: categoryColorOrDefault(c.color) }}
                    title={c.color}
                    aria-label={`Farbe ${c.color}`}
                  />
                </td>
                <td>{rentalPeriodLabel(c.rental_period)}</td>
                <td>{c.sort_order}</td>
                <td>{c.is_active ? "Aktiv" : "Inaktiv"}</td>
              </ClickableTableRow>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
